import React, { Component } from 'react';
import { ArrowRight, Shield, Package, Leaf } from 'lucide-react';
import { useLanguage } from '../hooks/useLanguage';
type HeroProps = {
  onOpenQuoteModal: () => void;
};
export function Hero({
  onOpenQuoteModal
}: HeroProps) {
  const {
    t
  } = useLanguage();
  const trustBadges = [{
    icon: Shield,
    label: t.hero.trustBadges.exportStandards
  }, {
    icon: Package,
    label: t.hero.trustBadges.traceablePackaging
  }, {
    icon: Leaf,
    label: t.hero.trustBadges.ethicalSourcing
  }];
  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const handleWhatsAppClick = () => {
    const message = encodeURIComponent('Hello, I would like to request a quote for kratom products.');
    window.open(`https://wa.me/6282275757589?text=${message}`, '_blank');
  };
  return <section id="hero" className="relative min-h-screen w-full bg-gradient-to-br from-emerald-50 via-white to-emerald-50 pt-20 md:pt-24" data-id="element-68">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5" data-id="element-69">
        <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-500 rounded-full blur-3xl" data-id="element-70" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-emerald-600 rounded-full blur-3xl" data-id="element-71" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20" data-id="element-72">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-73">
          {/* Left Content */}
          <div className="text-center lg:text-left" data-id="element-74">
            {/* Logo */}
            <div className="flex justify-center lg:justify-start mb-6" data-id="element-75">
              <img src="https://cdn.magicpatterns.com/uploads/xsY9W8NB5gHP7cfcSyxnu5/2.png" alt="KBIN - PT Kratom Borneo Indonesia" className="h-20 md:h-24 w-auto" data-id="element-76" />
            </div>

            {/* Tagline */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-800 rounded-full text-sm font-semibold mb-6" data-id="element-77">
              <Leaf className="w-4 h-4" data-id="element-78" />
              {t.hero.tagline}
            </div>

            {/* Headline */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight" data-id="element-79">
              {t.hero.headline}
            </h1>

            {/* Supporting Text */}
            <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-xl mx-auto lg:mx-0" data-id="element-80">
              {t.hero.supporting}
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-10" data-id="element-81">
              <button onClick={handleWhatsAppClick} className="px-8 py-4 bg-emerald-700 hover:bg-emerald-800 text-white font-semibold rounded-xl transition-all hover:shadow-lg hover:shadow-emerald-700/25 flex items-center justify-center gap-2" data-id="element-82">
                {t.hero.requestQuote}
                <ArrowRight className="w-5 h-5" data-id="element-83" />
              </button>
              <button onClick={scrollToContact} className="px-8 py-4 bg-white hover:bg-gray-50 text-emerald-700 font-semibold rounded-xl border-2 border-emerald-700 transition-colors flex items-center justify-center gap-2" data-id="element-84">
                {t.hero.contactUs}
              </button>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap gap-4 justify-center lg:justify-start" data-id="element-85">
              {trustBadges.map(badge => <div key={badge.label} className="flex items-center gap-2 px-4 py-2 bg-white rounded-xl shadow-md border border-gray-100" data-id="element-86">
                  <badge.icon className="w-5 h-5 text-emerald-600" data-id="element-87" />
                  <span className="text-sm font-medium text-gray-700" data-id="element-88">
                    {badge.label}
                  </span>
                </div>)}
            </div>
          </div>

          {/* Right Content - Hero Image */}
          <div className="relative flex justify-center lg:justify-end" data-id="element-89">
            <div className="relative" data-id="element-90">
              {/* Decorative Elements */}
              <div className="absolute -top-4 -left-4 w-24 h-24 bg-emerald-200 rounded-full opacity-50" data-id="element-91" />
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-emerald-300 rounded-full opacity-40" data-id="element-92" />

              {/* Main Image Card */}
              <div className="relative bg-white rounded-3xl shadow-2xl p-6 md:p-8" data-id="element-93">
                <img src="https://cdn.magicpatterns.com/uploads/t37N25GpwJa9GYRmF4gvMx/1.png" alt="KBIN Logo - Quality Kratom from Indonesia" className="w-64 md:w-80 h-auto" data-id="element-94" />
                <div className="mt-6 text-center" data-id="element-95">
                  <p className="text-emerald-800 font-bold text-xl" data-id="element-96">
                    {t.hero.companyName}
                  </p>
                  <p className="text-gray-500 text-sm mt-1" data-id="element-97">
                    {t.hero.established}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce" data-id="element-98">
        <div className="w-6 h-10 border-2 border-emerald-400 rounded-full flex justify-center pt-2" data-id="element-99">
          <div className="w-1.5 h-3 bg-emerald-400 rounded-full" data-id="element-100" />
        </div>
      </div>
    </section>;
}